import { useState, useCallback } from 'react';

import useApiRequest from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';

import { BookingItem } from 'hooks/useBookingItems';

interface Venue {
  id: number;
  name: string;
}

interface ClientService {
  id: number;
  name: string;
  disabled: boolean;
}

interface BookingHost {
  id: number;
  firstName: string;
  lastName: string;
  label: string;
  email: string;
  telephone: string;
}

export interface Booking {
  id: number;
  startTime: string;
  endTime: string;
  purchaseOrder: string;
  clientService: ClientService;
  venue: Venue;
  bookingItems: BookingItem[];
  bookingHost: BookingHost | null;
}

const useBooking = (id?: number, eagerLoad: boolean = true) => {
  const [booking, setBooking] = useState<Booking | undefined>(undefined);

  const apiRequest = useApiRequest();

  const requestValid = Number.isInteger(id) && id > 0 && eagerLoad;

  const clearData = useCallback(async (): undefined => {
    setBooking(undefined);
    return undefined;
  }, []);

  const fetchData = useCallback(async (): Booking => {
    const { data: nextBooking = {} } = ((await apiRequest.get(
      `/v1/bookings/${id}`
    )) as unknown) as { data: Booking };

    setBooking(nextBooking);
    return nextBooking;
  }, [apiRequest, id]);

  const updatePurchaseOrder = useCallback(
    async purchaseOrder => {
      const { data: booking } = await apiRequest.put(
        `/v1/bookings/${id}/purchase_order`,
        {
          purchaseOrder,
        }
      );

      setBooking(booking);

      return booking;
    },
    [apiRequest, id]
  );

  const update = useCallback(
    async (attributes = {}) => {
      if ('purchaseOrder' in attributes && Object.keys(attributes).length === 1)
        return updatePurchaseOrder(attributes.purchaseOrder);

      const { data: updatedBooking } = ((await apiRequest.put(
        `/v1/bookings/${id}`,
        {
          booking: attributes,
        }
      )) as unknown) as { data: Booking };

      setBooking(updatedBooking);

      return updatedBooking;
    },
    [apiRequest, updatePurchaseOrder, id]
  );

  const { loading, error, refresh } = useAsync<Booking | undefined>(
    requestValid ? fetchData : clearData,
    requestValid && eagerLoad
  );

  return {
    data: booking,
    loading,
    error,
    refresh,
    update,
  };
};

export default useBooking;
